import QRCode from 'qrcode';
import { VdpElement, ElementMaterialConfig, ElementMaterialFinish, ElementMaterialBumpMap } from '../types';
import { getStarSvgPath, getPolygonSvgPath } from './shapeAndFormatUtils';

export interface MaterialFinishPreset {
  id: ElementMaterialFinish;
  name: string;
  badge: string;
  icon: string;
  sheen: string;
  description: string;
  defaultMetalness: number;
  defaultRoughness: number;
  defaultRelief: number;
  defaultColor: string;
}

export const MATERIAL_FINISH_PRESETS: MaterialFinishPreset[] = [
  {
    id: 'default',
    name: 'Base Paper Finish',
    badge: 'Base Label',
    icon: '⚪',
    sheen: 'Inherit',
    description: 'Matches the main label paper substrate without special overlays.',
    defaultMetalness: 0.0,
    defaultRoughness: 0.45,
    defaultRelief: 0.0,
    defaultColor: '#ffffff'
  },
  {
    id: 'gold_foil',
    name: '24K Gold Hot Foil',
    badge: 'Gold Foil',
    icon: '🌟',
    sheen: 'High Mirror Foil',
    description: 'Rich hot-stamped 24K gold foil with intense specular reflection and crisp raised foil lip.',
    defaultMetalness: 0.96,
    defaultRoughness: 0.10,
    defaultRelief: 0.35,
    defaultColor: '#d4af37'
  },
  {
    id: 'silver_foil',
    name: 'Chrome Silver Foil',
    badge: 'Silver Foil',
    icon: '🪙',
    sheen: 'Mirror Chrome',
    description: 'Ultra-reflective polished chrome silver foil with high metallic luster.',
    defaultMetalness: 0.98,
    defaultRoughness: 0.08,
    defaultRelief: 0.35,
    defaultColor: '#e2e8f0'
  },
  {
    id: 'spot_gloss',
    name: 'Raised Spot UV Varnish',
    badge: 'Spot Gloss',
    icon: '✨',
    sheen: 'Wet Lacquer Gloss',
    description: 'High-shine transparent gloss varnish overlay that pops with tactile raised thickness.',
    defaultMetalness: 0.0,
    defaultRoughness: 0.02,
    defaultRelief: 0.45,
    defaultColor: '#ffffff'
  },
  {
    id: 'matte',
    name: 'Soft-Touch Velvet Matte',
    badge: 'Deep Matte',
    icon: '🪨',
    sheen: 'Diffuse Anti-Glare',
    description: 'Soft-touch velvety matte finish that gently diffuses light without glare.',
    defaultMetalness: 0.0,
    defaultRoughness: 0.90,
    defaultRelief: 0.0,
    defaultColor: '#ffffff'
  },
  {
    id: 'embossed',
    name: '3D Sculpted Emboss',
    badge: '3D Emboss',
    icon: '🏔️',
    sheen: 'Raised Extrusion',
    description: 'Physically raised 3D extrusion protruding outward from the label with beveled edges.',
    defaultMetalness: 0.0,
    defaultRoughness: 0.25,
    defaultRelief: 1.25,
    defaultColor: '#ffffff'
  },
  {
    id: 'debossed',
    name: '3D Stamped Deboss',
    badge: '3D Deboss',
    icon: '🕳️',
    sheen: 'Sunken Stamp',
    description: 'Physically stamped indentation pressed deeply into the label substrate.',
    defaultMetalness: 0.0,
    defaultRoughness: 0.35,
    defaultRelief: -1.25,
    defaultColor: '#ffffff'
  },
  {
    id: 'holographic',
    name: 'Holographic Rainbow Foil',
    badge: 'Holo Rainbow',
    icon: '🌈',
    sheen: 'Prismatic Laser',
    description: 'Color-shifting diffraction grating foil with vibrant rainbow spectrum sheen.',
    defaultMetalness: 0.95,
    defaultRoughness: 0.12,
    defaultRelief: 0.25,
    defaultColor: '#a855f7'
  },
  {
    id: 'custom_metallic',
    name: 'Custom Metallic Foil',
    badge: 'Custom Foil',
    icon: '🎨',
    sheen: 'Custom Calibrated',
    description: 'Fully customizable metallic tone with adjustable metalness, gloss, and relief.',
    defaultMetalness: 0.92,
    defaultRoughness: 0.12,
    defaultRelief: 0.30,
    defaultColor: '#b76e79'
  }
];

export const QUICK_FOIL_SWATCHES = [
  { name: '24K Royal Gold', color: '#d4af37' },
  { name: 'Champagne Gold', color: '#f3e5ab' },
  { name: 'Rose Gold', color: '#b76e79' },
  { name: 'Chrome Silver', color: '#e2e8f0' },
  { name: 'Burnished Copper', color: '#b45309' },
  { name: 'Electric Cyan', color: '#06b6d4' },
  { name: 'Emerald Green', color: '#059669' },
  { name: 'Ruby Crimson', color: '#e11d48' },
  { name: 'Deep Violet', color: '#9333ea' },
  { name: 'Obsidian Black', color: '#1e293b' }
];

/**
 * Creates an offscreen canvas sized with the exact aspect ratio of the 2D template.
 * This guarantees that scaleX === scaleY so circles, text, and artwork are NEVER stretched.
 */
export function createProportionalCanvas(templateWidth: number, templateHeight: number, maxDim = 1024): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const w = templateWidth && templateWidth > 0 ? templateWidth : 100;
  const h = templateHeight && templateHeight > 0 ? templateHeight : 100;
  const aspect = w / h;
  if (aspect >= 1.0) {
    canvas.width = maxDim;
    canvas.height = Math.max(64, Math.round(maxDim / aspect));
  } else {
    canvas.height = maxDim;
    canvas.width = Math.max(64, Math.round(maxDim * aspect));
  }
  return canvas;
}

/**
 * Creates a specular metallic gradient for gold, silver, holographic, or tinted foil
 */
export function createMetallicGradient(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  finish: ElementMaterialFinish,
  customColor?: string
): CanvasGradient | string {
  const grad = ctx.createLinearGradient(x, y, x + w, y + h);

  if (finish === 'gold_foil') {
    grad.addColorStop(0, '#fef9c3'); // Bright specular sheen
    grad.addColorStop(0.2, '#fef08a');
    grad.addColorStop(0.4, '#eab308');
    grad.addColorStop(0.65, '#ca8a04');
    grad.addColorStop(0.85, '#fef08a');
    grad.addColorStop(1, '#a16207');
    return grad;
  }

  if (finish === 'silver_foil') {
    grad.addColorStop(0, '#ffffff'); // Pure mirror reflection
    grad.addColorStop(0.25, '#94a3b8');
    grad.addColorStop(0.5, '#f8fafc');
    grad.addColorStop(0.75, '#cbd5e1');
    grad.addColorStop(1, '#64748b');
    return grad;
  }

  if (finish === 'holographic') {
    grad.addColorStop(0, '#ec4899'); // Pink
    grad.addColorStop(0.25, '#8b5cf6'); // Purple
    grad.addColorStop(0.5, '#06b6d4'); // Cyan
    grad.addColorStop(0.75, '#10b981'); // Green
    grad.addColorStop(1, '#f59e0b'); // Amber
    return grad;
  }

  if (finish === 'custom_metallic' || customColor) {
    const baseColor = customColor || '#b76e79';
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.2, baseColor);
    grad.addColorStop(0.5, '#ffffff');
    grad.addColorStop(0.8, baseColor);
    grad.addColorStop(1, '#1e293b');
    return grad;
  }

  return customColor || '#000000';
}

// Pattern Cache to prevent redundant canvas creation and improve 3D texture generation performance
const patternCache = new Map<string, CanvasPattern>();

/**
 * Generates an embossed / debossed tactile bump style (CanvasPattern or rgb fill)
 * representing micro-relief textures (leather, brushed metal, linen, hammered foil, etc.)
 */
export function generateBumpStyle(
  ctx: CanvasRenderingContext2D,
  baseBVal: number,
  bumpType: ElementMaterialBumpMap = 'none',
  scale: number = 1.0,
  repeat: number = 6,
  invert: boolean = false,
  bevelSoftness: number = 0.35
): string | CanvasPattern {
  if (bumpType === 'none' || scale <= 0) {
    return `rgb(${baseBVal}, ${baseBVal}, ${baseBVal})`;
  }

  const cacheKey = `${baseBVal}_${bumpType}_${scale}_${repeat}_${invert}_${bevelSoftness}`;
  if (patternCache.has(cacheKey)) {
    return patternCache.get(cacheKey)!;
  }

  try {
    const tileSize = Math.max(12, Math.min(64, Math.round(48 / Math.max(1, repeat * 0.25))));
    const pCanvas = document.createElement('canvas');
    pCanvas.width = tileSize;
    pCanvas.height = tileSize;
    const pCtx = pCanvas.getContext('2d');
    if (!pCtx) return `rgb(${baseBVal}, ${baseBVal}, ${baseBVal})`;

    // Base height level
    pCtx.fillStyle = `rgb(${baseBVal}, ${baseBVal}, ${baseBVal})`;
    pCtx.fillRect(0, 0, tileSize, tileSize);

    const delta = Math.round(28 * scale) * (invert ? -1 : 1);

    if (bumpType === 'brushed_metal') {
      for (let py = 0; py < tileSize; py += 2) {
        const noise = ((py * 17) % 19 - 9) / 9;
        const v = Math.max(0, Math.min(255, baseBVal + Math.round(noise * delta)));
        pCtx.fillStyle = `rgb(${v}, ${v}, ${v})`;
        pCtx.fillRect(0, py, tileSize, 1.5);
      }
    } else if (bumpType === 'hammered_foil') {
      const cells = 3;
      const step = tileSize / cells;
      for (let cx = 0; cx < cells; cx++) {
        for (let cy = 0; cy < cells; cy++) {
          const rad = (step * 0.45);
          const centerOffset = ((cx * 7 + cy * 13) % 5) - 2;
          const centerX = cx * step + step / 2 + centerOffset;
          const centerY = cy * step + step / 2 - centerOffset;
          const grad = pCtx.createRadialGradient(centerX, centerY, 0, centerX, centerY, rad);
          const peak = Math.max(0, Math.min(255, baseBVal + delta));
          grad.addColorStop(0, `rgb(${peak}, ${peak}, ${peak})`);
          grad.addColorStop(0.7, `rgb(${baseBVal}, ${baseBVal}, ${baseBVal})`);
          const rim = Math.max(0, Math.min(255, baseBVal - Math.round(delta * 0.6)));
          grad.addColorStop(1, `rgb(${rim}, ${rim}, ${rim})`);
          pCtx.fillStyle = grad;
          pCtx.beginPath();
          pCtx.arc(centerX, centerY, rad, 0, Math.PI * 2);
          pCtx.fill();
        }
      }
    } else if (bumpType === 'micro_diamond') {
      const half = tileSize / 2;
      pCtx.beginPath();
      pCtx.moveTo(half, 0);
      pCtx.lineTo(tileSize, half);
      pCtx.lineTo(half, tileSize);
      pCtx.lineTo(0, half);
      pCtx.closePath();
      const peak = Math.max(0, Math.min(255, baseBVal + delta));
      pCtx.fillStyle = `rgb(${peak}, ${peak}, ${peak})`;
      pCtx.fill();
      const rim = Math.max(0, Math.min(255, baseBVal - Math.round(delta * 0.5)));
      pCtx.strokeStyle = `rgb(${rim}, ${rim}, ${rim})`;
      pCtx.lineWidth = 1;
      pCtx.stroke();
    } else if (bumpType === 'fine_leather') {
      for (let i = 0; i < 24; i++) {
        const px = ((i * 37) % tileSize);
        const py = ((i * 53) % tileSize);
        const pr = 1.5 + ((i * 11) % 3);
        const v = Math.max(0, Math.min(255, baseBVal + (i % 2 === 0 ? delta : -Math.round(delta * 0.7))));
        pCtx.fillStyle = `rgb(${v}, ${v}, ${v})`;
        pCtx.beginPath();
        pCtx.arc(px, py, pr, 0, Math.PI * 2);
        pCtx.fill();
      }
    } else if (bumpType === 'linen_canvas') {
      for (let i = 0; i < tileSize; i += 3) {
        const v1 = Math.max(0, Math.min(255, baseBVal + Math.round(delta * 0.7)));
        pCtx.fillStyle = `rgb(${v1}, ${v1}, ${v1})`;
        pCtx.fillRect(i, 0, 1.2, tileSize);
        const v2 = Math.max(0, Math.min(255, baseBVal - Math.round(delta * 0.6)));
        pCtx.fillStyle = `rgb(${v2}, ${v2}, ${v2})`;
        pCtx.fillRect(0, i, tileSize, 1.2);
      }
    } else if (bumpType === 'carbon_fiber') {
      const q = tileSize / 4;
      for (let row = 0; row < 4; row++) {
        for (let col = 0; col < 4; col++) {
          const isLight = (row + col) % 2 === 0;
          const v = Math.max(0, Math.min(255, baseBVal + (isLight ? delta : -delta)));
          pCtx.fillStyle = `rgb(${v}, ${v}, ${v})`;
          pCtx.fillRect(col * q, row * q, q, q);
        }
      }
    } else if (bumpType === 'noise_grain') {
      const imgData = pCtx.getImageData(0, 0, tileSize, tileSize);
      const data = imgData.data;
      for (let i = 0; i < data.length; i += 4) {
        const n = (Math.random() - 0.5) * 2 * delta;
        const val = Math.max(0, Math.min(255, baseBVal + Math.round(n)));
        data[i] = val;
        data[i + 1] = val;
        data[i + 2] = val;
        data[i + 3] = 255;
      }
      pCtx.putImageData(imgData, 0, 0);
    } else if (bumpType === 'smooth_bevel') {
      const grad = pCtx.createRadialGradient(
        tileSize / 2, tileSize / 2, tileSize * Math.max(0.1, (1 - bevelSoftness)) * 0.5,
        tileSize / 2, tileSize / 2, tileSize * 0.5
      );
      const peak = Math.max(0, Math.min(255, baseBVal + delta));
      const rim = Math.max(0, Math.min(255, baseBVal - Math.round(delta * 0.5)));
      grad.addColorStop(0, `rgb(${peak}, ${peak}, ${peak})`);
      grad.addColorStop(1, `rgb(${rim}, ${rim}, ${rim})`);
      pCtx.fillStyle = grad;
      pCtx.fillRect(0, 0, tileSize, tileSize);
    }

    const pattern = ctx.createPattern(pCanvas, 'repeat');
    if (pattern) {
      patternCache.set(cacheKey, pattern);
      return pattern;
    }
    return `rgb(${baseBVal}, ${baseBVal}, ${baseBVal})`;
  } catch (err) {
    return `rgb(${baseBVal}, ${baseBVal}, ${baseBVal})`;
  }
}

export interface RenderMultiMapParams {
  labelCanvas: HTMLCanvasElement;
  roughnessCanvas: HTMLCanvasElement;
  metalnessCanvas: HTMLCanvasElement;
  bumpCanvas: HTMLCanvasElement;
  template: any;
  csvData: any;
  elementMaterials?: Record<string, ElementMaterialConfig>;
  globalFinish?: string;
  globalEmbossMode?: 'none' | 'embossed' | 'debossed' | 'spot_uv';
  globalReliefHeight?: number;
  highlightElementId?: string | null;
}

/**
 * Renders Diffuse (color), Roughness, Metalness, and 3D Bump/Displacement maps
 * simultaneously across all sticker elements. Supports async image loading to prevent
 * missing textures in 3D preview.
 */
export async function renderTemplateMultiMapToCanvases({
  labelCanvas,
  roughnessCanvas,
  metalnessCanvas,
  bumpCanvas,
  template,
  csvData,
  elementMaterials = {},
  globalFinish = 'gloss',
  globalEmbossMode = 'none',
  globalReliefHeight = 1.2,
  highlightElementId = null
}: RenderMultiMapParams): Promise<void> {
  const width = labelCanvas.width;
  const height = labelCanvas.height;

  const dCtx = labelCanvas.getContext('2d', { alpha: true });
  const rCtx = roughnessCanvas.getContext('2d', { alpha: false });
  const mCtx = metalnessCanvas.getContext('2d', { alpha: false });
  const bCtx = bumpCanvas.getContext('2d', { alpha: false });

  if (!dCtx || !rCtx || !mCtx || !bCtx) return;

  // Track image loading tasks to await them before completing render
  const imageTasks: Promise<void>[] = [];

  const scaleX = width / (template.width || 100);
  const scaleY = height / (template.height || 100);

  // 1. Base Label Substrate Initialization
  dCtx.clearRect(0, 0, width, height);
  dCtx.fillStyle = template.backgroundColor || template.bgColor || '#ffffff';
  dCtx.fillRect(0, 0, width, height);

  // Roughness: 0 (super glossy) to 255 (super matte)
  let baseRoughness = 115; // default ~0.45
  if (globalFinish === 'gloss') baseRoughness = 45; // ~0.18
  else if (globalFinish === 'matte') baseRoughness = 220; // ~0.86
  else if (globalFinish === 'satin') baseRoughness = 95; // ~0.37
  else if (globalFinish === 'clear_transparent') baseRoughness = 25; // ~0.10
  rCtx.fillStyle = `rgb(${baseRoughness}, ${baseRoughness}, ${baseRoughness})`;
  rCtx.fillRect(0, 0, width, height);

  // Metalness: 0 (non-metal) to 255 (pure conductive metal)
  let baseMetalness = 0;
  if (globalFinish === 'gold_foil' || globalFinish === 'silver_foil') {
    baseMetalness = 245;
  }
  mCtx.fillStyle = `rgb(${baseMetalness}, ${baseMetalness}, ${baseMetalness})`;
  mCtx.fillRect(0, 0, width, height);

  // Bump / Height: Neutral gray 128 (0mm displacement)
  bCtx.fillStyle = 'rgb(128, 128, 128)';
  bCtx.fillRect(0, 0, width, height);

  const sortedElements: VdpElement[] = [...(template.elements || [])]
    .filter(el => !el.disabled && !el.isSkipped)
    .sort((a, b) => ((a as any).zIndex || 0) - ((b as any).zIndex || 0));

  sortedElements.forEach(el => {
    // Resolve element material config
    const customConfig = elementMaterials[el.id] || el.materialConfig || (el.materialFinish ? {
      finish: el.materialFinish,
      metalness: el.materialMetalness,
      roughness: el.materialRoughness,
      reliefHeight: el.materialRelief,
      color: el.materialColor
    } : null);

    const finish: ElementMaterialFinish = customConfig?.finish || 'default';
    const preset = MATERIAL_FINISH_PRESETS.find(p => p.id === finish) || MATERIAL_FINISH_PRESETS[0];

    const effMetalness = customConfig?.metalness !== undefined ? customConfig.metalness : preset.defaultMetalness;
    const effRoughness = customConfig?.roughness !== undefined ? customConfig.roughness : preset.defaultRoughness;
    const effRelief = customConfig?.reliefHeight !== undefined ? customConfig.reliefHeight : preset.defaultRelief;
    const effColor = customConfig?.color || preset.defaultColor;

    // Metalness & Roughness byte values
    const mVal = Math.max(0, Math.min(255, Math.round(effMetalness * 255)));
    const rVal = Math.max(0, Math.min(255, Math.round(effRoughness * 255)));

    // Height/Bump byte value (128 = 0mm, 255 = +2mm, 0 = -2mm)
    let bVal = 128;
    if (finish === 'default' && globalEmbossMode !== 'none') {
      if (globalEmbossMode === 'embossed') bVal = Math.min(255, 128 + Math.round(globalReliefHeight * 60));
      else if (globalEmbossMode === 'debossed') bVal = Math.max(0, 128 - Math.round(globalReliefHeight * 60));
      else if (globalEmbossMode === 'spot_uv') bVal = Math.min(255, 128 + 35);
    } else if (finish !== 'default') {
      bVal = Math.max(0, Math.min(255, 128 + Math.round(effRelief * 65)));
    }

    const x = (el.x || 0) * scaleX;
    const y = (el.y || 0) * scaleY;
    const w = (el.width || 40) * scaleX;
    const h = (el.height || 40) * scaleY;

    // Apply transforms to all 4 canvas contexts
    [dCtx, rCtx, mCtx, bCtx].forEach(ctx => {
      ctx.save();
      if (el.rotation) {
        ctx.translate(x + w / 2, y + h / 2);
        ctx.rotate((el.rotation * Math.PI) / 180);
        ctx.translate(-(x + w / 2), -(y + h / 2));
      }
      if (el.opacity !== undefined && !isNaN(Number(el.opacity))) {
        ctx.globalAlpha = Number(el.opacity);
      }
    });

    // Content resolution for dynamic text
    const fallbackText = el.content ?? el.text ?? el.staticValue ?? el.value ?? (el as any).name ?? '';
    let val = fallbackText;
    if (el.columnName && el.columnName.trim() !== '') {
      const colName = el.columnName.trim();
      if (csvData && csvData.rows && csvData.rows[0] && csvData.rows[0][colName]) {
        val = csvData.rows[0][colName];
      } else if (!fallbackText) {
        val = `[${colName}]`;
      }
    }
    if (!val && el.type === 'text') {
      val = 'Sample Text';
    }

    if (['text', 'currency', 'date', 'serial', 'uppercase'].includes(el.type) && typeof val === 'string') {
      if ((el as any).textCase === 'uppercase' || el.type === 'uppercase') {
        val = val.toUpperCase();
      } else if ((el as any).textCase === 'lowercase') {
        val = val.toLowerCase();
      } else if ((el as any).textCase === 'capitalize') {
        val = val.replace(/\b\w/g, c => c.toUpperCase());
      }
      if ((el as any).prefix) val = `${(el as any).prefix}${val}`;
      if ((el as any).suffix) val = `${val}${(el as any).suffix}`;
    }

    // Determine diffuse fill
    let diffuseFill: CanvasGradient | string = el.color || '#000000';
    if (['gold_foil', 'silver_foil', 'holographic', 'custom_metallic'].includes(finish)) {
      diffuseFill = createMetallicGradient(dCtx, x, y, w, h, finish, effColor);
    } else if (finish === 'embossed' && effMetalness > 0.4) {
      diffuseFill = createMetallicGradient(dCtx, x, y, w, h, 'custom_metallic', effColor);
    }

    const roughnessFill = `rgb(${rVal}, ${rVal}, ${rVal})`;
    const metalnessFill = `rgb(${mVal}, ${mVal}, ${mVal})`;

    const bumpMapType: ElementMaterialBumpMap = customConfig?.bumpMapType || (
      finish === 'embossed' || finish === 'debossed' ? 'smooth_bevel' : 'none'
    );
    const bumpScale = customConfig?.bumpScale !== undefined ? customConfig.bumpScale : 1.0;
    const bumpRepeat = customConfig?.bumpRepeat !== undefined ? customConfig.bumpRepeat : 6;
    const invertBump = customConfig?.invertBump !== undefined ? customConfig.invertBump : (finish === 'debossed');
    const bevelSoftness = customConfig?.bevelSoftness !== undefined ? customConfig.bevelSoftness : 0.35;

    const bumpFill = generateBumpStyle(bCtx, bVal, bumpMapType, bumpScale, bumpRepeat, invertBump, bevelSoftness);

    // Helper to draw text across all maps
    const drawTextMap = (ctx: CanvasRenderingContext2D, fill: CanvasGradient | string | CanvasPattern) => {
      const fontSize = (el.fontSize || 14) * scaleY;
      const fontFamily = el.fontFamily || 'Helvetica, Arial, sans-serif';
      const fontStyle = (el as any).isItalic ? 'italic' : 'normal';
      const fontWeight = (el as any).isBold ? 'bold' : 'normal';

      ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
      ctx.fillStyle = fill;
      ctx.textAlign = (el.textAlign as CanvasTextAlign) || 'left';
      ctx.textBaseline = 'top';

      let textX = x;
      if (el.textAlign === 'center') textX = x + w / 2;
      else if (el.textAlign === 'right') textX = x + w;

      let displayVal = String(val);
      if (el.type === 'currency' && Number(val)) {
        displayVal = `${(el as any).currencySymbol || '$'}${Number(val).toFixed(2)}`;
      }

      ctx.fillText(displayVal, textX, y);
    };

    // Helper to draw rectangle across all maps
    const drawRectMap = (ctx: CanvasRenderingContext2D, fill: CanvasGradient | string | CanvasPattern) => {
      ctx.fillStyle = fill;
      const cr = (el as any).cornerRadius ? (el as any).cornerRadius * scaleX : 0;
      if (cr > 0 && typeof (ctx as any).roundRect === 'function') {
        ctx.beginPath();
        (ctx as any).roundRect(x, y, w, h, cr);
        ctx.fill();
      } else {
        ctx.fillRect(x, y, w, h);
      }
    };

    // Helper to draw circle across all maps
    const drawCircleMap = (ctx: CanvasRenderingContext2D, fill: CanvasGradient | string | CanvasPattern) => {
      ctx.fillStyle = fill;
      ctx.beginPath();
      ctx.arc(x + w / 2, y + h / 2, Math.min(w, h) / 2, 0, 2 * Math.PI);
      ctx.fill();
    };

    // Helper to draw star across all maps
    const drawStarMap = (ctx: CanvasRenderingContext2D, fill: CanvasGradient | string | CanvasPattern) => {
      try {
        const pathData = getStarSvgPath(w, h, (el as any).pointsCount || 5, (el as any).innerRatio || 0.4);
        const path = new Path2D(pathData);
        ctx.save();
        ctx.translate(x, y);
        ctx.fillStyle = fill;
        ctx.fill(path);
        ctx.restore();
      } catch (e) {
        drawRectMap(ctx, fill);
      }
    };

    // Helper to draw polygon across all maps
    const drawPolygonMap = (ctx: CanvasRenderingContext2D, fill: CanvasGradient | string | CanvasPattern) => {
      try {
        const pathData = getPolygonSvgPath(w, h, (el as any).sidesCount || 6);
        const path = new Path2D(pathData);
        ctx.save();
        ctx.translate(x, y);
        ctx.fillStyle = fill;
        ctx.fill(path);
        ctx.restore();
      } catch (e) {
        drawRectMap(ctx, fill);
      }
    };

    // Execute drawing by element type
    if (['text', 'currency', 'date', 'serial', 'uppercase'].includes(el.type)) {
      drawTextMap(dCtx, diffuseFill);
      drawTextMap(rCtx, roughnessFill);
      drawTextMap(mCtx, metalnessFill);
      drawTextMap(bCtx, bumpFill);
    } else if (el.type === 'rectangle') {
      drawRectMap(dCtx, diffuseFill);
      drawRectMap(rCtx, roughnessFill);
      drawRectMap(mCtx, metalnessFill);
      drawRectMap(bCtx, bumpFill);
    } else if (el.type === 'circle') {
      drawCircleMap(dCtx, diffuseFill);
      drawCircleMap(rCtx, roughnessFill);
      drawCircleMap(mCtx, metalnessFill);
      drawCircleMap(bCtx, bumpFill);
    } else if (el.type === 'star') {
      drawStarMap(dCtx, diffuseFill);
      drawStarMap(rCtx, roughnessFill);
      drawStarMap(mCtx, metalnessFill);
      drawStarMap(bCtx, bumpFill);
    } else if (el.type === 'polygon') {
      drawPolygonMap(dCtx, diffuseFill);
      drawPolygonMap(rCtx, roughnessFill);
      drawPolygonMap(mCtx, metalnessFill);
      drawPolygonMap(bCtx, bumpFill);
    } else if (el.type === 'qr') {
      try {
        const ecl = (['L', 'M', 'Q', 'H'].includes((el as any).qrErrorCorrection || '') ? (el as any).qrErrorCorrection : 'M') as 'L' | 'M' | 'Q' | 'H';
        const qrData = QRCode.create(val || 'https://example.com', { errorCorrectionLevel: ecl });
        const size = qrData.modules.size;
        const modules = qrData.modules.data;
        const cellW = w / size;
        const cellH = h / size;

        const drawQrModules = (ctx: CanvasRenderingContext2D, fill: CanvasGradient | string | CanvasPattern) => {
          ctx.fillStyle = fill;
          for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
              if (modules[r * size + c] === 1) {
                ctx.fillRect(x + c * cellW, y + r * cellH, cellW + 0.1, cellH + 0.1);
              }
            }
          }
        };

        if (!(el as any).isTransparentBg) {
          dCtx.fillStyle = (el as any).backgroundColor || '#ffffff';
          dCtx.fillRect(x, y, w, h);
          rCtx.fillStyle = `rgb(${baseRoughness}, ${baseRoughness}, ${baseRoughness})`;
          rCtx.fillRect(x, y, w, h);
          mCtx.fillStyle = 'rgb(0,0,0)';
          mCtx.fillRect(x, y, w, h);
          bCtx.fillStyle = 'rgb(128,128,128)';
          bCtx.fillRect(x, y, w, h);
        }

        drawQrModules(dCtx, diffuseFill);
        drawQrModules(rCtx, roughnessFill);
        drawQrModules(mCtx, metalnessFill);
        drawQrModules(bCtx, bumpFill);
      } catch (e) {
        drawRectMap(dCtx, diffuseFill);
      }
    } else if (el.type === 'barcode' || el.type === 'ean13' || el.type === 'upca') {
      const drawBarcodeModules = (ctx: CanvasRenderingContext2D, fill: CanvasGradient | string | CanvasPattern) => {
        ctx.fillStyle = fill;
        const bars = 24;
        const barW = w / bars;
        for (let i = 0; i < bars; i++) {
          if (i % 3 !== 1) {
            ctx.fillRect(x + i * barW, y, barW * 0.8, h * 0.7);
          }
        }
        ctx.font = `${h * 0.22}px monospace`;
        ctx.textAlign = 'center';
        ctx.fillText(val || '123456789', x + w / 2, y + h * 0.75);
      };

      dCtx.fillStyle = '#ffffff';
      dCtx.fillRect(x, y, w, h);
      drawBarcodeModules(dCtx, diffuseFill);
      drawBarcodeModules(rCtx, roughnessFill);
      drawBarcodeModules(mCtx, metalnessFill);
      drawBarcodeModules(bCtx, bumpFill);
    } else if (el.type === 'image') {
      if ((el as any).src || el.staticValue) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        const imgTask = new Promise<void>((resolve) => {
          img.onload = () => {
            dCtx.drawImage(img, x, y, w, h);
            resolve();
          };
          img.onerror = () => {
            console.warn(`Failed to load image for 3D preview: ${el.id}`);
            drawRectMap(dCtx, '#f1f5f9');
            resolve();
          };
        });
        
        imageTasks.push(imgTask);
        img.src = (el as any).src || el.staticValue || '';
      } else {
        drawRectMap(dCtx, '#f1f5f9');
      }
      drawRectMap(rCtx, roughnessFill);
      drawRectMap(mCtx, metalnessFill);
      drawRectMap(bCtx, bumpFill);
    }

    // Optional active element contour outline highlight
    if (highlightElementId === el.id) {
      dCtx.save();
      dCtx.strokeStyle = '#f59e0b';
      dCtx.lineWidth = 3;
      dCtx.setLineDash([6, 3]);
      dCtx.strokeRect(x - 2, y - 2, w + 4, h + 4);
      dCtx.restore();
    }

    [dCtx, rCtx, mCtx, bCtx].forEach(ctx => ctx.restore());
  });

  // Await all async image renders before returning to ensure textures are complete
  if (imageTasks.length > 0) {
    await Promise.all(imageTasks);
  }
}

export interface UvProjectParams {
  srcCanvas: HTMLCanvasElement;
  destCanvas: HTMLCanvasElement;
  selectedModel: string;
  boxStickerFace: string;
  templateWidth: number;
  templateHeight: number;
  uvScale: number;
  uvX: number;
  uvY: number;
  uvRotation: number;
  uvRepeatX: number;
  uvRepeatY: number;
  wrapAround: boolean;
  clearColor?: string;
}

/**
 * Projects a 1024x1024 flat sticker map onto the 2048x2048 Three.js UV canvas
 * with identical aspect ratio correction, scale, and repeat grid.
 */
export function projectCanvasToUvMap({
  srcCanvas,
  destCanvas,
  selectedModel,
  boxStickerFace,
  templateWidth,
  templateHeight,
  uvScale,
  uvX,
  uvY,
  uvRotation,
  uvRepeatX,
  uvRepeatY,
  wrapAround,
  clearColor = 'transparent'
}: UvProjectParams): void {
  const destCtx = destCanvas.getContext('2d');
  if (!destCtx) return;

  if (clearColor === 'transparent') {
    destCtx.clearRect(0, 0, destCanvas.width, destCanvas.height);
  } else {
    destCtx.fillStyle = clearColor;
    destCtx.fillRect(0, 0, destCanvas.width, destCanvas.height);
  }

  const aspect = (templateWidth || 100) / (templateHeight || 100);
  const isBoxSingleFace = selectedModel === 'box' && boxStickerFace !== 'all';

  let uvStretchFactor = 1.0;
  if (selectedModel === 'can') {
    const R = 0.9;
    const H = 2.8;
    uvStretchFactor = (2 * Math.PI * R) / H;
  } else if (selectedModel === 'pouch') {
    const R = 0.95;
    const H = 2.8;
    uvStretchFactor = (2 * Math.PI * R) / H;
  } else if (selectedModel === 'jar') {
    const R = 1.2;
    const H = 1.4;
    uvStretchFactor = (2 * Math.PI * R) / H;
  } else if (selectedModel === 'cup') {
    const R = 0.95;
    const H = 2.4;
    uvStretchFactor = (2 * Math.PI * R) / H;
  } else if (selectedModel === 'box') {
    let faceW = 2.2;
    let faceH = 2.6;
    if (boxStickerFace === 'left' || boxStickerFace === 'right') {
      faceW = 1.6;
      faceH = 2.6;
    } else if (boxStickerFace === 'top' || boxStickerFace === 'bottom') {
      faceW = 2.2;
      faceH = 1.6;
    }
    uvStretchFactor = faceW / faceH;
  } else {
    // Glass Beverage Bottle
    const R = 0.88;
    const H = 2.2;
    uvStretchFactor = (2 * Math.PI * R) / H;
  }

  let drawW: number;
  let drawH: number;

  // We ensure the physical 3D aspect ratio exactly matches the 2D template aspect ratio:
  // Aspect_3D = (drawW / drawH) * uvStretchFactor = aspect.
  // Therefore: (drawW / drawH) = aspect / uvStretchFactor.
  const targetUvAspect = aspect / uvStretchFactor;

  if (wrapAround && !isBoxSingleFace) {
    drawW = 2048 * uvScale;
    drawH = drawW / targetUvAspect;
  } else {
    const baseBox = 1024 * uvScale;
    if (targetUvAspect >= 1.0) {
      drawW = baseBox;
      drawH = baseBox / targetUvAspect;
    } else {
      drawH = baseBox;
      drawW = baseBox * targetUvAspect;
    }
  }

  const drawX = (uvX / 100) * 2048;
  const drawY = (uvY / 100) * 2048;

  destCtx.save();
  destCtx.translate(drawX, drawY);
  if (uvRotation !== 0) {
    destCtx.rotate((uvRotation * Math.PI) / 180);
  }

  for (let rx = 0; rx < uvRepeatX; rx++) {
    for (let ry = 0; ry < uvRepeatY; ry++) {
      const xOffset = -drawW / 2 + rx * drawW;
      const yOffset = -drawH / 2 + ry * drawH;
      destCtx.drawImage(srcCanvas, xOffset, yOffset, drawW, drawH);
    }
  }
  destCtx.restore();
}
